import { Badge } from '@parrot-software/design-system';
import { cn } from '@parrot-software/utils';
import { useTranslation } from 'react-i18next';

import { VerificationStatus } from '@personnel/domain/types';
import type { VerificationStatusType } from '@personnel/domain/types';

export type VerificationBadgeProps = {
  /**
   * The state the backend published. An absent value renders nothing: a response that never
   * mentioned verification says neither that the account is verified nor that it is pending.
   */
  verificationStatus?: VerificationStatusType;
  className?: string;
};

/**
 * Whether every contact value of a user is confirmed, as a badge.
 *
 * It takes the state and not a boolean so that a third value, if one ever appears, can be painted
 * without changing how every caller passes it.
 *
 * @param {VerificationBadgeProps} props - Component properties
 */
export const VerificationBadge = ({ verificationStatus, className }: VerificationBadgeProps) => {
  const { t } = useTranslation('personnel-employees');

  if (!verificationStatus) return null;

  const isVerified = verificationStatus === VerificationStatus.VERIFIED;

  return (
    <Badge
      className={cn(
        'w-fit font-sans text-xs font-semibold',
        {
          'border-success bg-bg-success text-success': isVerified,
          // There is no background token for destructive, so the tint is the color at a tenth.
          'border-destructive bg-destructive/10 text-destructive': !isVerified,
        },
        className,
      )}
      data-state={isVerified ? 'verified' : 'unverified'}
      data-testid="verification-badge"
      variant="outline"
    >
      {t(isVerified ? 'verificationBadge.verified' : 'verificationBadge.unverified')}
    </Badge>
  );
};
